import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, MoreHorizontal, CalendarClock } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { APPT_STATUSES, apptMeta } from "@/config/crm";
import { AppointmentFormDialog } from "@/components/appointments/AppointmentFormDialog";

export default function Appointments() {
  const [appts, setAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date, setDate] = useState("");
  const [doctor, setDoctor] = useState("all");
  const [status, setStatus] = useState("all");
  const [bookOpen, setBookOpen] = useState(false);
  const [reschedule, setReschedule] = useState(null);
  const [rDate, setRDate] = useState(""); const [rTime, setRTime] = useState("");

  useEffect(() => { api.get("/doctors").then(({ data }) => setDoctors(data.doctors)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (date) params.date = date;
    if (doctor !== "all") params.doctor = doctor;
    if (status !== "all") params.status = status;
    try { const { data } = await api.get("/appointments", { params }); setAppts(data.appointments); }
    finally { setLoading(false); }
  }, [date, doctor, status]);
  useEffect(() => { load(); }, [load]);

  const setStatusFor = async (id, s) => {
    try { await api.put(`/appointments/${id}`, { status: s }); toast.success("Status updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  const cancel = async (id) => {
    try { await api.put(`/appointments/${id}/cancel`); toast.success("Appointment cancelled — slot freed"); load(); }
    catch { toast.error("Cancel failed"); }
  };
  const doReschedule = async () => {
    if (!rDate || !rTime) { toast.error("Pick date and time"); return; }
    try { await api.put(`/appointments/${reschedule.id}/reschedule`, { date: rDate, start_time: rTime }); toast.success("Rescheduled"); setReschedule(null); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Reschedule failed"); }
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="appointments-page">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900">Appointments</h1>
          <p className="mt-1 text-sm text-slate-500"><span className="font-medium text-slate-700">{appts.length}</span> appointments</p>
        </div>
        <Button onClick={() => setBookOpen(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="book-appointment-button"><Plus className="h-4 w-4" /> Book Appointment</Button>
      </div>

      <Card className="p-4 shadow-sm">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="sm:w-48" data-testid="appt-filter-date" />
          <Select value={doctor} onValueChange={setDoctor}><SelectTrigger className="sm:w-52" data-testid="appt-filter-doctor"><SelectValue placeholder="Doctor" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Doctors</SelectItem>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent></Select>
          <Select value={status} onValueChange={setStatus}><SelectTrigger className="sm:w-44" data-testid="appt-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Statuses</SelectItem>{APPT_STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select>
          {(date || doctor !== "all" || status !== "all") && <Button variant="ghost" onClick={() => { setDate(""); setDoctor("all"); setStatus("all"); }}>Clear</Button>}
        </div>
      </Card>

      <Card className="hidden overflow-hidden shadow-sm md:block">
        <Table>
          <TableHeader>
            <TableRow className="border-slate-200 hover:bg-transparent">
              <TableHead className="pl-6 text-xs uppercase tracking-wider text-slate-400">Patient</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Service</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Doctor</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">When</TableHead>
              <TableHead className="text-xs uppercase tracking-wider text-slate-400">Status</TableHead>
              <TableHead className="pr-6 text-right text-xs uppercase tracking-wider text-slate-400">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? [...Array(4)].map((_, i) => <TableRow key={i}><TableCell colSpan={6} className="px-6"><Skeleton className="h-8 w-full" /></TableCell></TableRow>)
              : appts.length === 0 ? <TableRow><TableCell colSpan={6} className="py-16 text-center text-sm text-slate-400" data-testid="appts-empty">No appointments.</TableCell></TableRow>
              : appts.map((a) => (
                <TableRow key={a.id} className="border-slate-100" data-testid="appt-row">
                  <TableCell className="pl-6 font-medium text-slate-900">{a.patient_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.service_name}</TableCell>
                  <TableCell className="text-sm text-slate-600">{a.doctor_name}</TableCell>
                  <TableCell className="font-mono text-xs text-slate-600">{a.date} · {a.start_time}–{a.end_time}</TableCell>
                  <TableCell><Badge className={cn(apptMeta(a.status).badge)}>{apptMeta(a.status).label}</Badge></TableCell>
                  <TableCell className="pr-6 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`appt-actions-${a.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setReschedule(a); setRDate(a.date); setRTime(a.start_time); }}>Reschedule</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatusFor(a.id, "confirmed")}>Mark Confirmed</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatusFor(a.id, "completed")}>Mark Completed</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setStatusFor(a.id, "no_show")}>Mark No-show</DropdownMenuItem>
                        <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => cancel(a.id)}>Cancel</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Card>

      <div className="space-y-3 md:hidden">
        {appts.map((a) => (
          <Card key={a.id} className="p-4 shadow-sm" data-testid="appt-card">
            <div className="flex items-start justify-between">
              <div><p className="font-medium text-slate-900">{a.patient_name}</p><p className="text-xs text-slate-500">{a.service_name} · {a.doctor_name}</p></div>
              <Badge className={cn(apptMeta(a.status).badge)}>{apptMeta(a.status).label}</Badge>
            </div>
            <p className="mt-2 flex items-center gap-1 font-mono text-xs text-slate-600"><CalendarClock className="h-3.5 w-3.5" /> {a.date} · {a.start_time}</p>
          </Card>
        ))}
      </div>

      <AppointmentFormDialog open={bookOpen} onOpenChange={setBookOpen} onBooked={load} />

      <Dialog open={Boolean(reschedule)} onOpenChange={(v) => !v && setReschedule(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="reschedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reschedule Appointment</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={rDate} onChange={(e) => setRDate(e.target.value)} data-testid="reschedule-date" /></div>
            <div className="space-y-1.5"><Label>Start time</Label><Input type="time" value={rTime} onChange={(e) => setRTime(e.target.value)} data-testid="reschedule-time" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReschedule(null)}>Cancel</Button>
            <Button onClick={doReschedule} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="reschedule-submit">Reschedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
